import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

const statusBadge = (status) => {
  if (status) {
    return <span className="badge bg-primary">Active</span>;
  } else {
    return <span className="badge bg-secondary">Inactive</span>;
  }
};

const Index = () => {
  const navigate = useNavigate();
  const [cashbackData, setCashbackData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });

  const fetchCashbackData = async (page = 1, limit = 10) => {
    try {
      const token = localStorage.getItem('token');
      const myHeaders = new Headers();
      myHeaders.append("Authorization", `Bearer ${token}`);

      const requestOptions = {
        method: "GET",
        headers: myHeaders,
        redirect: "follow"
      };

      const response = await fetch(`https://api.experts4u.in/api/v1/cashback?page=${page}&limit=${limit}`, requestOptions);
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Failed to fetch cashbacks');
      }

      if (result.success) {
        setCashbackData(result.data);
        setPagination(result.pagination);
      }
    } catch (error) {
      setError(error.message);
      console.error('Error fetching cashback data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCashbackData();
  }, []);

  const handlePageChange = (newPage) => {
    fetchCashbackData(newPage, pagination.itemsPerPage);
  };

  const handleEdit = (id) => {
    navigate(`/cashback/edit/${id}`);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const myHeaders = new Headers();
      myHeaders.append("Authorization", `Bearer ${token}`);

      const requestOptions = {
        method: "DELETE",
        headers: myHeaders,
        redirect: "follow"
      };

      const response = await fetch(`https://api.experts4u.in/api/v1/cashback/${id}`, requestOptions);
      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to delete cashback');
      }

      Swal.fire({
        icon: 'success',
        title: 'Deleted!',
        text: 'Cashback has been deleted.',
        showConfirmButton: false,
        timer: 1500
      });
      fetchCashbackData(pagination.currentPage, pagination.itemsPerPage);
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error deleting cashback: ' + error.message
      });
    }
  };

  return (
    <div className="container-fluid py-3 py-md-5 mt-2 mt-md-4 bg-light min-vh-100">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="fw-bold mb-0">Cashback</h4>
        <Link to="/cashback/add" className="btn btn-primary">
          <i className="bi bi-plus-lg me-2"></i>Add New
        </Link>
      </div>
      {error && <div className="alert alert-danger">{error}</div>}
      <div className="card rounded-3">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div>
              <label>Show
                <select 
                  className="form-select d-inline-block w-auto mx-2"
                  onChange={(e) => fetchCashbackData(1, parseInt(e.target.value))}
                >
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                </select>
                entries
              </label>
            </div>
            <div>
              <input type="text" className="form-control" placeholder="Search:" />
            </div>
          </div>
          <div className="table-responsive">
            <table className="table table-bordered align-middle mb-0">
              <thead className="table-primary">
                <tr>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Cashback Value</th>
                  <th>Max Cashback</th>
                  <th>Minimum Spend</th>
                  <th>Status</th>
                  <th>Created At</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="8" className="text-center">Loading...</td>
                  </tr>
                ) : cashbackData.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center">No data available</td>
                  </tr>
                ) : (
                  cashbackData.map((row) => (
                    <tr key={row._id}>
                      <td>{new Date(row.startDate).toLocaleDateString()}</td>
                      <td>{new Date(row.endDate).toLocaleDateString()}</td>
                      <td>% {row.cashbackValue}</td>
                      <td>₹ {row.maxCashback}</td>
                      <td>₹ {row.minimumSpend}</td>
                      <td>{statusBadge(row.isActive)}</td>
                      <td>{new Date(row.createdAt).toLocaleDateString()}</td>
                      <td>
                        <button 
                          className="btn btn-primary btn-sm me-2" 
                          title="Edit"
                          onClick={() => handleEdit(row._id)}
                        >
                          <i className="fa fa-edit"></i>
                        </button>
                        <button 
                          className="btn btn-danger btn-sm" 
                          title="Delete"
                          onClick={() => handleDelete(row._id)}
                        >
                          <i className="fa fa-trash"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="d-flex justify-content-between align-items-center mt-2">
            <div>
              Showing {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1} to {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)} of {pagination.totalItems} entries
            </div>
            <nav>
              <ul className="pagination mb-0">
                <li className={`page-item ${pagination.currentPage === 1 ? 'disabled' : ''}`}>
                  <button 
                    className="page-link" 
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    disabled={pagination.currentPage === 1}
                  >
                    Previous
                  </button>
                </li>
                {[...Array(pagination.totalPages)].map((_, index) => (
                  <li key={index + 1} className={`page-item ${pagination.currentPage === index + 1 ? 'active' : ''}`}>
                    <button 
                      className="page-link"
                      onClick={() => handlePageChange(index + 1)}
                    >
                      {index + 1}
                    </button>
                  </li>
                ))}
                <li className={`page-item ${pagination.currentPage === pagination.totalPages ? 'disabled' : ''}`}>
                  <button 
                    className="page-link"
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    disabled={pagination.currentPage === pagination.totalPages}
                  >
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Index